import { useEffect } from 'react';
import styled from 'styled-components';
import { InAppContentDefinition } from 'modules/graphql';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { NegativeFill } from 'ui/icons';
import { FormField } from 'ui/formField';
import { colors } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import {
  FieldNames,
  getIsInvalidHexColor,
  SettingsReadOnlyText,
} from 'modules/templateEditor/common';
import { useErrorValidationContext } from 'modules/templateEditor/context/ErrorValidationContext';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { ColorPicker } from './shared/ColorPicker';
import { constructFieldErrors, getIsEditMode } from '../../../common/utils';
import { SETTINGS_DISPLAY_CONTENT } from '../../constants';
import { formatHexForState, formatRokuHexForUI } from '../../utils';

const StyledContainer = styled.div`
  width: ${pxToRem(160)};
`;

export const RokuBackgroundColor = () => {
  const { view } = useQueryParamContext();
  const { liveTemplate, setCurrentMediumField, isEditMode } = useEditableFieldsContext();
  const { ottPayload } = (liveTemplate?.mediumData || {}) as InAppContentDefinition;
  const { localeContentErrors, localeContentWarnings } = useErrorValidationContext();

  const prettifiedHexValue = formatRokuHexForUI(ottPayload?.backgroundColor);
  const cssHexValue = formatHexForState(prettifiedHexValue);
  const hexColorError = getIsInvalidHexColor(cssHexValue);

  const isEditModeVisible = getIsEditMode(view, isEditMode);

  const { hasError: hasRokuBgColorError } = constructFieldErrors({
    contentErrors: localeContentErrors,
    contentWarnings: localeContentWarnings,
    fieldName: FieldNames.ROKU_BACKGROUND_COLOR,
  });

  const nonEditModeText = (
    <SettingsReadOnlyText
      $hasError={hasRokuBgColorError}
      isUnset={false}
      data-test="roku-background-color"
    >
      {hasRokuBgColorError && <NegativeFill color={colors.red100} height={16} width={16} />}
      {ottPayload?.backgroundColor
        ? formatRokuHexForUI(ottPayload.backgroundColor)
        : SETTINGS_DISPLAY_CONTENT.roku.backgroundColor.unset}
    </SettingsReadOnlyText>
  );

  useEffect(() => {
    if (!ottPayload?.backgroundColor && isEditModeVisible)
      setCurrentMediumField('ottPayload', {
        ...ottPayload,
        backgroundColor: SETTINGS_DISPLAY_CONTENT.roku.backgroundColor.defaultColor,
      });
  }, [isEditModeVisible]);

  return (
    <FormField
      isHorizontalLayout
      hasError={hexColorError ? hasRokuBgColorError : undefined}
      labelText={SETTINGS_DISPLAY_CONTENT.roku.backgroundColor.label}
      data-test="form-field-roku-bg-color"
    >
      <StyledContainer>
        {isEditMode ? (
          <ColorPicker
            hexColor={ottPayload?.backgroundColor}
            onChange={(hexColor) =>
              setCurrentMediumField('ottPayload', { ...ottPayload, backgroundColor: hexColor })
            }
            dataTestIdentifier={'background-color'}
          />
        ) : (
          nonEditModeText
        )}
      </StyledContainer>
    </FormField>
  );
};
